import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import TopBar from "@/components/TopBar";
import MembershipCard from "@/components/MembershipCard";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Gift, ArrowRight, TrendingUp, Sparkles, Calendar, Download, Wallet } from "lucide-react";
import { toast } from "sonner";

const txIcon = (t) => ({ earn: "+", redeem: "−", bonus: "+", adjustment: "±" }[t] || "·");

export default function PatientDashboard() {
  const { slug } = useParams();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [appleAvailable, setAppleAvailable] = useState(false);

  const load = async () => {
    const { data } = await api.get("/patient/dashboard");
    setData(data);
  };
  useEffect(() => {
    load();
    api.get("/wallet/apple/available").then(({ data }) => setAppleAvailable(!!data.available)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen bg-mr-bg">
        <TopBar tenant={{ name: "MedRewards", slug }} />
        <div className="p-10 grid lg:grid-cols-3 gap-6">
          <Skeleton className="h-64 rounded-2xl" /><Skeleton className="h-64 rounded-2xl" /><Skeleton className="h-64 rounded-2xl" />
        </div>
      </div>
    );
  }

  const { tenant, tiers, next_tier, recent_transactions, featured_rewards, user: u } = data;
  const currentTierObj = tiers.find((t) => t.name === u.tier);
  const currentThreshold = currentTierObj?.threshold || 0;
  const nextThreshold = next_tier?.threshold || (currentThreshold + 1000);
  const progressPct = Math.min(100, Math.max(0, ((u.points - currentThreshold) / Math.max(1, nextThreshold - currentThreshold)) * 100));

  const redeem = async (id) => {
    try {
      await api.post(`/patient/redeem/${id}`);
      toast.success("Reward redeemed!");
      await load();
      await refresh();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not redeem"));
    }
  };

  const downloadCard = async () => {
    try {
      const resp = await api.get("/patient/me/card.png", { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "image/png" });
      const filename = `medrewards-card-${(u.id || "").slice(-8).toLowerCase()}.png`;
      const file = new File([blob], filename, { type: "image/png" });

      // iOS Safari (and modern Android Chrome) — use the Web Share API so the user
      // gets the native share sheet with "Save Image" / "Save to Photos" options.
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: "My MedRewards card",
            text: "My loyalty membership card",
          });
          return;
        } catch (e) {
          // User cancelled the share sheet — that's fine, just no toast/error.
          if (e?.name === "AbortError") return;
          // Otherwise fall through to the desktop download path
        }
      }

      // Desktop / fallback — classic file download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Card downloaded");
    } catch (e) {
      toast.error("Could not download card");
    }
  };

  const addToAppleWallet = async () => {
    try {
      // iOS Safari only hands a .pkpass to Wallet when it arrives via a direct
      // navigation (not a JS Blob download). Mint a one-time download URL and
      // navigate to it.
      const { data } = await api.post("/patient/me/pass-link");
      if (!data?.url) throw new Error("No download URL");
      window.location.href = data.url;
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not generate Apple Wallet pass"));
    }
  };

  return (
    <div className="min-h-screen bg-mr-bg">
      <TopBar
        tenant={tenant}
        unread={data.unread_notifications}
        links={[
          { to: `/c/${slug}/dashboard`, label: "Dashboard" },
          { to: `/c/${slug}/rewards`, label: "Rewards" },
          { to: `/c/${slug}/family`, label: "Family" },
          { to: `/c/${slug}/refer`, label: "Refer" },
          { to: `/c/${slug}/profile`, label: "Profile" },
        ]}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Card + tier */}
          <div className="lg:col-span-7 space-y-8 animate-fade-up">
            <div className="flex flex-col sm:flex-row gap-8 items-start">
              <div className="flex flex-col items-start gap-3 w-full max-w-[460px]">
                <MembershipCard tenant={tenant} user={u} customImagePath={currentTierObj?.card_image_path} />
                <div className="flex flex-wrap gap-2">
                  <Button
                    data-testid="dashboard-download-card-btn"
                    variant="outline"
                    size="sm"
                    onClick={downloadCard}
                    className="rounded-full border-mr"
                  >
                    <Download className="h-3.5 w-3.5 mr-1.5" /> Download as image
                  </Button>
                  {appleAvailable && (
                    <Button
                      data-testid="dashboard-add-to-wallet-btn"
                      size="sm"
                      onClick={addToAppleWallet}
                      className="rounded-full bg-black hover:bg-black/90 text-white"
                    >
                      <Wallet className="h-3.5 w-3.5 mr-1.5" /> Add to Apple Wallet
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex-1">
                <p className="label-eyebrow text-mr-secondary">Total points</p>
                <p className="font-display text-5xl font-bold text-mr-primary mt-1" data-testid="dashboard-points">{u.points.toLocaleString()}</p>
                <div className="mt-6">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-mr-primary font-medium">{u.tier}</span>
                    {next_tier ? <span className="text-mr-secondary">{(next_tier.threshold - u.points).toLocaleString()} pts to {next_tier.name}</span> : <Badge className="rounded-full bg-mr-accent text-white">Top tier 🎉</Badge>}
                  </div>
                  <Progress value={progressPct} className="h-2" />
                </div>
                {currentTierObj?.perks?.length > 0 && (
                  <div className="mt-6 space-y-2">
                    <p className="label-eyebrow text-mr-secondary">Your perks</p>
                    <ul className="space-y-1.5">
                      {currentTierObj.perks.map((p, i) => (
                        <li key={i} className="text-sm text-mr-primary flex items-start gap-2"><Sparkles className="h-3.5 w-3.5 mt-0.5 text-mr-accent" /> {p}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Recent activity */}
            <div className="bg-white rounded-2xl border border-mr p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-xl font-semibold text-mr-primary">Recent activity</h2>
                <Link to={`/c/${slug}/profile`} className="text-sm text-mr-accent flex items-center gap-1">View all <ArrowRight className="h-3 w-3" /></Link>
              </div>
              {recent_transactions.length === 0 && <p className="text-mr-secondary text-sm py-6 text-center">No activity yet — your earned points will show here.</p>}
              <ul className="divide-y divide-mr-border">
                {recent_transactions.map((tx) => (
                  <li key={tx.id} data-testid={`tx-${tx.id}`} className="py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-mr-primary">{tx.reason}</p>
                      <p className="text-xs text-mr-secondary mt-0.5">{new Date(tx.created_at).toLocaleString()}</p>
                    </div>
                    <span className={`font-display font-semibold text-base ${tx.points > 0 ? "text-mr-success" : "text-mr-accent"}`}>
                      {txIcon(tx.type)} {Math.abs(tx.points)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Rewards + referrals */}
          <div className="lg:col-span-5 space-y-6 animate-fade-up" style={{ animationDelay: "100ms" }}>
            <div className="bg-white rounded-2xl border border-mr p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-xl font-semibold text-mr-primary">Featured rewards</h2>
                <Button data-testid="dashboard-view-rewards-btn" size="sm" variant="ghost" className="rounded-full text-mr-accent" onClick={() => navigate(`/c/${slug}/rewards`)}>
                  Browse all <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
              <div className="space-y-3">
                {featured_rewards.map((r) => {
                  const can = u.points >= r.cost;
                  return (
                    <div key={r.id} data-testid={`featured-reward-${r.id}`} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-mr hover:bg-[#F8FAFC] transition-colors">
                      <div className="h-10 w-10 rounded-lg bg-mr-bg flex items-center justify-center"><Gift className="h-5 w-5 text-mr-accent" /></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-mr-primary truncate">{r.title}</p>
                        <p className="text-xs text-mr-secondary">{r.cost.toLocaleString()} pts · {r.category}</p>
                      </div>
                      <Button
                        data-testid={`redeem-btn-${r.id}`}
                        size="sm"
                        disabled={!can}
                        onClick={() => redeem(r.id)}
                        className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)] disabled:opacity-40"
                      >
                        Redeem
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#0F8964] to-[#0EA572] text-white rounded-2xl p-6 relative overflow-hidden grain">
              <p className="label-eyebrow text-white/70">Refer & earn</p>
              <h3 className="font-display text-2xl font-semibold mt-2">Give friends 500 pts, get 500 pts.</h3>
              <p className="text-white/75 text-sm mt-2">Share your code and earn bonus points when they complete their first appointment.</p>
              <div className="mt-5 flex items-center gap-2">
                <code data-testid="dashboard-referral-code" className="bg-white/15 text-white px-4 py-2 rounded-full font-mono text-sm">{u.referral_code}</code>
                <Button
                  size="sm"
                  data-testid="dashboard-copy-referral-btn"
                  className="rounded-full bg-mr-accent hover:bg-[var(--mr-accent-hover)]"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/c/${slug}/register?ref=${u.referral_code}`);
                    toast.success("Referral link copied");
                  }}
                >Copy link</Button>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-mr p-6">
              <div className="flex items-center gap-2 mb-3"><TrendingUp className="h-4 w-4 text-mr-accent" /><h3 className="font-display text-lg font-semibold text-mr-primary">Tier ladder</h3></div>
              <ul className="space-y-2">
                {tiers.map((t) => {
                  const reached = u.points >= t.threshold;
                  const isCurrent = t.name === u.tier;
                  return (
                    <li key={t.id} className={`flex items-center justify-between p-2.5 rounded-lg ${isCurrent ? "bg-[#E2E8F0]" : ""}`}>
                      <div className="flex items-center gap-3">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: t.color }} />
                        <span className={`text-sm ${reached ? "text-mr-primary font-medium" : "text-mr-secondary"}`}>{t.name}</span>
                      </div>
                      <span className="text-xs text-mr-secondary">{t.threshold.toLocaleString()} pts</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
